// Shared formatting/derivation helpers for the Plan 005 §4.6 domain
// inventory surfaces (`domains-register.html`) — used by DomainRow.vue (all
// three densities), DomainsRegisterBoard.vue, DomainDetailDrawer.vue and
// DomainPickerModal.vue so state language, expiry thresholds and captions
// can never drift between them. Mirrors `serverFormat.js`'s
// plain-functions-module shape.

export const USAGE_STATES = ['in_use', 'parked', 'abandoned', 'unknown'];

const STATE_LABELS = {
  in_use: 'In use',
  parked: 'Parked',
  abandoned: 'Abandoned',
  unknown: 'Unknown',
};

const STATE_MODIFIERS = {
  in_use: 'inuse',
  parked: 'parked',
  abandoned: 'abandoned',
  unknown: 'unknown',
};

const VIA_LABELS = {
  enom: 'Enom',
  cloudflare: 'Cloudflare-only',
  manual: 'Manual',
  pipeline: 'Pipeline (reclaimed)',
};

export function stateLabel(state) {
  return STATE_LABELS[state] || state || '—';
}

export function stateModifier(state) {
  return STATE_MODIFIERS[state] || 'unknown';
}

export function discoveredViaLabel(via) {
  return VIA_LABELS[via] || (via ? via : '—');
}

export function registrarLabel(domain) {
  if (!domain.registrar) {
    // A Cloudflare-only or manual row genuinely has no registrar on file.
    return domain.discovered_via === 'cloudflare' ? 'Not on Enom' : '—';
  }
  return domain.registrar === 'enom'
    ? 'Enom'
    : domain.registrar.charAt(0).toUpperCase() + domain.registrar.slice(1);
}

export function formatDate(epochSeconds) {
  if (!epochSeconds) return null;
  const d = new Date(epochSeconds * 1000);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function formatDateTime(epochSeconds) {
  if (!epochSeconds) return null;
  const d = new Date(epochSeconds * 1000);
  const pad = (n) => String(n).padStart(2, '0');
  return `${formatDate(epochSeconds)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function formatShortDate(epochSeconds) {
  if (!epochSeconds) return null;
  return new Date(epochSeconds * 1000).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' });
}

export function relativeTime(epochSeconds) {
  if (!epochSeconds) return null;
  const seconds = Math.max(0, Math.floor(Date.now() / 1000) - epochSeconds);
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h ago`;
  return `${Math.round(seconds / 86400)}d ago`;
}

/** Whole days from now to the given epoch (negative = already past). */
export function daysUntil(epochSeconds) {
  if (!epochSeconds) return null;
  return Math.floor((epochSeconds - Date.now() / 1000) / 86400);
}

export function expiryRelLabel(epochSeconds) {
  const days = daysUntil(epochSeconds);
  if (days === null) return null;
  if (days < 0) return `expired ${Math.abs(days)}d ago`;
  if (days === 0) return 'expires today';
  return `in ${days} days`;
}

/**
 * Expiry tone per the mockup's threshold language: >60 days plain, 15–60
 * days the reserved warning treatment, <15 days danger (and bold). Expired
 * is danger too.
 */
export function expiryTone(epochSeconds) {
  const days = daysUntil(epochSeconds);
  if (days === null) return '';
  if (days < 15) return 'danger';
  if (days <= 60) return 'warn';
  return '';
}

/**
 * Manual-override caption per the approved design: a lock icon plus
 * "manual · <user> · <date>". The caller renders the icon; this returns the
 * text.
 */
export function manualCaption(domain) {
  const parts = ['manual'];
  if (domain.usage_state_set_by_name) parts.push(domain.usage_state_set_by_name);
  if (domain.usage_state_set_at) parts.push(formatShortDate(domain.usage_state_set_at));
  return parts.join(' · ');
}

/**
 * Short under-chip caption for a DERIVED state ("derived", "derived ·
 * reclaim pool", "derived · source dropped out", ...).
 */
export function derivedCaption(domain) {
  if (domain.abandoned_by_run) return 'derived · reclaim pool';
  if (domain.review_flagged_at) return 'derived · source dropped out';
  if (domain.usage_state === 'unknown' && domain.discovered_via === 'manual') return 'derived · manual-only entry';
  return 'derived';
}

/**
 * One-line derivation reason for the sub-line under the domain name — the
 * "why" visible without opening the drawer. NOTE (deliberate, flagged in the
 * mockup's anatomy notes): the M3 backend does not persist a derivation
 * reason, so this reconstructs the most plausible §4.6 rule from the row's
 * own facts. It can only ever be as specific as those facts allow.
 */
export function derivationReason(domain) {
  if (domain.usage_state_source === 'manual') {
    return 'manual override — a human\'s call beats the heuristic';
  }
  if (domain.abandoned_by_run) {
    return `abandoned by run #${domain.abandoned_by_run}`;
  }
  switch (domain.usage_state) {
    case 'in_use':
      if (domain.site_id) return `derived: linked site #${domain.site_id}`;
      if (domain.zone_ref) return 'derived: live DNS records on the zone';
      return 'derived: referenced by the fleet or a provisioning run';
    case 'parked':
      return 'derived: resolves to nothing meaningful';
    case 'unknown':
      if (domain.review_flagged_at) return 'derived: source dropped out';
      if (!domain.zone_ref) {
        return domain.discovered_via === 'manual'
          ? 'manual entry — no zone/DNS read possible yet'
          : 'derived: no zone to read';
      }
      return 'derived: DNS read failed';
    case 'abandoned':
      return 'marked abandoned';
    default:
      return '';
  }
}
